/**
 * Helper for emitting AI workflow metrics using state-based detection.
 */

import { trackStructEvent } from "@snowplow/browser-tracker";

import { EmbeddedMetric, Logger } from "@/apis/Logger";

import type {
  AgentCoreOperationResult,
  AgentResponse,
} from "../@types/agentcore";
import { Day1EventAction, Day1EventCategory } from "../@types/snowplow-events";

interface WorkflowMetrics {
  iterations: number;
  duration: number;
}

/**
 * AI Workflow metrics helper using state-based detection
 */
export class AIWorkflowMetricsHelper {
  /**
   * Map agent type to Snowplow event category
   */
  private static getEventCategory(agentType: string): Day1EventCategory {
    switch (agentType.toLowerCase()) {
      case "vpn":
        return Day1EventCategory.ConnectToVpn;
      default:
        return Day1EventCategory.ConnectToVpn; // Fallback
    }
  }

  /**
   * Emit workflow metrics based on agent result.
   * This is the main method for emitting terminal state metrics (COMPLETED, FAILED, TIMEOUT).
   * Each terminal state emits:
   * - EMF CloudWatch metrics (via Katal Logger)
   * - Snowplow events
   * - With duration and iterations
   *
   * Also handles infrastructure errors (result.success = false) such as:
   * - Network errors
   * - Authentication failures
   * - IPC failures
   */
  static emitWorkflowMetrics(
    result: AgentCoreOperationResult,
    agentType: string,
    metrics: WorkflowMetrics
  ): void {
    const { iterations, duration } = metrics;
    const metricPrefix = agentType.toUpperCase();

    if (result.success && result.response) {
      this.emitAgentStateMetrics(result.response, {
        agentType,
        metricPrefix,
        iterations,
        duration,
      });
    } else {
      // Handle infrastructure/client errors (network, auth, IPC failures)
      const error = result.success === false ? result.error : "Unknown error";

      Logger.publishErrorMetric(
        new EmbeddedMetric(`${metricPrefix}WorkflowClientError`, "Count", 1, {
          AgentType: agentType,
        }),
        "Client-layer workflow error",
        undefined,
        { agentType, error, duration, iterations }
      );

      trackStructEvent({
        category: this.getEventCategory(agentType),
        action: Day1EventAction.AIWorkflowClientError,
      });

      void Logger.error("Client-layer workflow error", undefined, {
        agentType,
        error,
        duration,
        iterations,
      });
    }
  }

  /**
   * Emit metrics based on agent response state.
   * All terminal states (COMPLETED, FAILED, TIMEOUT, CANCELLED) emit:
   * - Duration metric (Milliseconds)
   * - Iterations metric (Count)
   * - State-specific metric (Count)
   * - Snowplow events for duration, iterations, and state
   */
  private static emitAgentStateMetrics(
    response: AgentResponse,
    context: {
      agentType: string;
      metricPrefix: string;
      iterations: number;
      duration: number;
    }
  ): void {
    const { agentType, metricPrefix, iterations, duration } = context;
    const category = this.getEventCategory(agentType);

    Logger.publishInfoMetric(
      new EmbeddedMetric(
        `${metricPrefix}WorkflowDuration`,
        "Milliseconds",
        duration,
        {
          AgentType: agentType,
        }
      ),
      "AI workflow duration",
      undefined,
      { agentType, iterations, duration, state: response.state }
    );

    Logger.publishInfoMetric(
      new EmbeddedMetric(
        `${metricPrefix}WorkflowIterations`,
        "Count",
        iterations,
        {
          AgentType: agentType,
        }
      ),
      "AI workflow iterations",
      undefined,
      { agentType, iterations, duration, state: response.state }
    );

    trackStructEvent({
      category,
      action: Day1EventAction.AIWorkflowDuration,
      value: duration,
    });

    trackStructEvent({
      category,
      action: Day1EventAction.AIWorkflowIterations,
      value: iterations,
    });

    switch (response.state) {
      case "COMPLETED":
        Logger.publishInfoMetric(
          new EmbeddedMetric(`${metricPrefix}WorkflowSuccess`, "Count", 1, {
            AgentType: agentType,
          }),
          "Workflow completed successfully",
          undefined,
          { agentType, iterations, duration }
        );

        trackStructEvent({
          category,
          action: Day1EventAction.AIWorkflowCompleted,
        });

        void Logger.info(`Workflow completed successfully`, undefined, {
          agentType,
          iterations,
          duration,
        });
        break;

      case "FAILED":
        Logger.publishInfoMetric(
          new EmbeddedMetric(`${metricPrefix}WorkflowFailed`, "Count", 1, {
            AgentType: agentType,
          }),
          "Workflow failed at agent layer",
          undefined,
          {
            agentType,
            message: response.message,
            iterations,
            duration,
          }
        );

        trackStructEvent({
          category,
          action: Day1EventAction.AIWorkflowFailed,
        });

        void Logger.error(`Workflow failed at agent layer`, undefined, {
          agentType,
          message: response.message,
          iterations,
          duration,
        });
        break;

      case "TIMEOUT":
        Logger.publishInfoMetric(
          new EmbeddedMetric(`${metricPrefix}AgentTimeout`, "Count", 1, {
            AgentType: agentType,
          }),
          "Agent workflow timeout",
          undefined,
          {
            agentType,
            iterations,
            duration,
          }
        );

        trackStructEvent({
          category,
          action: Day1EventAction.AIWorkflowTimeout,
        });

        void Logger.warn(`Agent workflow timeout`, undefined, {
          agentType,
          iterations,
          duration,
        });
        break;

      case "CANCELLED":
        Logger.publishInfoMetric(
          new EmbeddedMetric(`${metricPrefix}WorkflowCancelled`, "Count", 1, {
            AgentType: agentType,
          }),
          "AI workflow cancelled",
          undefined,
          { agentType, iterations, duration }
        );

        trackStructEvent({
          category,
          action: Day1EventAction.AIWorkflowCancelled,
        });

        void Logger.info(`AI workflow cancelled`, undefined, {
          agentType,
          iterations,
          duration,
        });
        break;

      case "IN_PROGRESS":
        Logger.publishInfoMetric(
          new EmbeddedMetric(
            `${metricPrefix}WorkflowUnexpectedInProgress`,
            "Count",
            1,
            {
              AgentType: agentType,
            }
          ),
          "Unexpected IN_PROGRESS state in final result",
          undefined,
          { agentType }
        );
        void Logger.error(
          `Unexpected IN_PROGRESS state in final result`,
          undefined,
          {
            agentType,
          }
        );
        break;

      default:
        Logger.publishInfoMetric(
          new EmbeddedMetric(
            `${metricPrefix}WorkflowUnknownState`,
            "Count",
            1,
            {
              AgentType: agentType,
            }
          ),
          "Unknown agent state",
          undefined,
          { agentType, state: response.state }
        );
        void Logger.error(`Unknown agent state`, undefined, {
          agentType,
          state: response.state,
        });
    }
  }

  /**
   * Emit workflow started metric (no duration/iterations yet)
   */
  static emitWorkflowStarted(agentType: string): void {
    const metricPrefix = agentType.toUpperCase();
    Logger.publishInfoMetric(
      new EmbeddedMetric(`${metricPrefix}WorkflowStarted`, "Count", 1, {
        AgentType: agentType,
      }),
      "AI workflow started",
      undefined,
      { agentType }
    );

    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIWorkflowStarted,
    });
  }

  /**
   * Emit workflow cancelled metric (user stopped the automation).
   * Optional duration parameter for cases where we track how long before cancellation.
   */
  static emitWorkflowCancelled(agentType: string, duration?: number): void {
    const metricPrefix = agentType.toUpperCase();
    Logger.publishInfoMetric(
      new EmbeddedMetric(`${metricPrefix}WorkflowCancelled`, "Count", 1, {
        AgentType: agentType,
      }),
      "AI workflow cancelled",
      undefined,
      { agentType, duration }
    );

    if (duration !== undefined) {
      Logger.publishInfoMetric(
        new EmbeddedMetric(
          `${metricPrefix}WorkflowDuration`,
          "Milliseconds",
          duration,
          {
            AgentType: agentType,
          }
        ),
        "AI workflow duration before cancellation",
        undefined,
        { agentType, duration }
      );

      trackStructEvent({
        category: this.getEventCategory(agentType),
        action: Day1EventAction.AIWorkflowDuration,
        value: duration,
      });
    }

    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIWorkflowCancelled,
    });
  }

  /**
   * Emit AI opt-in metric (user chose AI automation)
   */
  static emitAIOptIn(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIOptInSelected,
    });
  }

  /**
   * Emit AI opt-out metric (user chose manual process)
   */
  static emitAIOptOut(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIOptOutSelected,
    });
  }

  /**
   * Emit AI control consent checked (user allows AI to control screen)
   */
  static emitAIControlConsentChecked(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIControlConsentChecked,
    });
  }

  /**
   * Emit AI control consent unchecked (user revokes AI screen control)
   */
  static emitAIControlConsentUnchecked(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIControlConsentUnchecked,
    });
  }

  /**
   * Emit screenshot data storage consent checked (user allows screenshot storage)
   */
  static emitScreenshotDataStorageConsentChecked(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.ScreenshotDataStorageConsentChecked,
    });
  }

  /**
   * Emit screenshot data storage consent unchecked (user revokes screenshot storage)
   */
  static emitScreenshotDataStorageConsentUnchecked(agentType: string): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.ScreenshotDataStorageConsentUnchecked,
    });
  }

  /**
   * Emit back to setup metric (user clicked "Back to setup" after automation failure)
   */
  static emitBackToSetup(agentType: string, failureCount: number): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIWorkflowBackToSetup,
      value: failureCount,
    });
  }

  /**
   * Emit manual fallback metric (user clicked "Set up manually" after repeated failures)
   */
  static emitManualFallback(agentType: string, failureCount: number): void {
    trackStructEvent({
      category: this.getEventCategory(agentType),
      action: Day1EventAction.AIWorkflowManualFallback,
      value: failureCount,
    });
  }
}
